import { Component, type ErrorInfo, type ReactNode } from "react";
import { buildBugReport, submitBugReport } from "../../platform/bugReports";
import { captureScreenshot } from "../../platform/screenshot";
import { t } from "../../i18n/t";

/**
 * G-72 — the screen died, and the report goes anyway.
 *
 * THE BUGS THIS CATCHES ARE THE ONES YOU OTHERWISE NEVER HEAR ABOUT. When an
 * uncaught error unmounts the tree, a florist mid-service does not compose a
 * bug report — they restart the app and carry on, and the crash is gone. So
 * this files one itself, with nobody typing: kind AUTOMATIC, the error, the
 * stack, and the console tail that led to it.
 *
 * WHY IT HAS TO BE A CLASS: React only offers `componentDidCatch` and
 * `getDerivedStateFromError` on class components. There is no hook equivalent,
 * and this is the one place in this codebase that needs one.
 *
 * FILED ONCE PER CRASH, not once per render. React re-renders the fallback, and
 * a boundary that reported on every pass would turn one bug into a thousand
 * rows and a queue that never drains.
 *
 * It does NOT try to be clever about recovery. The tree under it is in an
 * unknown state, so the only honest offer is to start the screen again.
 */

interface Props {
  children: ReactNode;
  /** Which kiosk panel was open when it died. */
  screen?: string;
}

interface State {
  crashed: boolean;
  filed: boolean;
}

export class ShopFloorErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false, filed: false };

  static getDerivedStateFromError(): Partial<State> {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (this.state.filed) return;
    this.setState({ filed: true });

    const report = buildBugReport({
      // Empty message is the marker of an AUTOMATIC report — the server stores
      // it as such and the owner's list can tell the two apart at a glance.
      message: "",
      kind: "AUTOMATIC",
      screen: this.props.screen,
      errorMessage: `${error.name}: ${error.message}`,
      // The component stack says WHICH panel blew up, which the JS stack often
      // does not once the bundle is minified.
      errorStack: [error.stack, info.componentStack]
        .filter(Boolean)
        .join("\n---\n")
        .slice(0, 20_000),
    });

    /**
     * Capture the crashed screen too.
     *
     * `captureScreenshot` photographs the WebView natively, so it sees the
     * fallback below rather than the panel that died — React has already
     * unmounted that. Still worth having: it shows WHICH kiosk view was open
     * and what else was on screen around it, and on a crash the owner has
     * nothing else to look at. It returns null in a browser and never throws.
     *
     * Not awaited, because `componentDidCatch` is not async and the screen has
     * already failed — a reporter that fails on top of a failure helps nobody.
     */
    void captureScreenshot().then((shot) => submitBugReport(report, shot));
  }

  render(): ReactNode {
    if (!this.state.crashed) return this.props.children;

    return (
      <div className="sf-crash" role="alert">
        <h2 className="sf-crash-title">{t("crash.title")}</h2>
        {/* Says the report already went, so nobody feels obliged to write one
            while a customer waits — and nobody assumes it was lost either. */}
        <p className="sf-crash-copy">{t("crash.copy")}</p>
        <button
          type="button"
          className="button"
          onClick={() => window.location.reload()}
        >
          {t("crash.restart")}
        </button>
      </div>
    );
  }
}
